import React from "react";
import { motion } from "framer-motion";
import { BorderBeam } from "../magicUi/BorderBeam";

const SustainableLiving = () => {
  const amenities = [
    { title: "Developer", value: "Emaar", icon: "🏢" },
    { title: "Location", value: "The Valley", icon: "📍" },
    { title: "Starting Price", value: "Upon Request", icon: "💰" },
    { title: "Bedrooms", value: "4 BR", icon: "🛏️" },
    { title: "Handover", value: "2029", icon: "📅" },
  ];

  return (
    <section
      className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#f8f9fa] to-[#e9ecef] py-16 md:py-24"
      id="sustainable-living"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute h-2 w-2 rounded-full bg-gradient-to-r from-[#d2ac47] to-[#b08e22]"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.3,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              x: [0, Math.random() * 100 - 50],
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-gradient-to-br from-[#d2ac47]/20 to-[#b08e22]/20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
      <motion.div
        className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-gradient-to-tr from-[#b08e22]/20 to-[#d2ac47]/20 blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
          delay: 1,
        }}
      />

      <div className="container relative mx-auto px-4">
        {/* Header */}
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="mb-6 inline-flex items-center"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-[#d2ac47]" />
            <span className="mx-4 text-sm font-semibold uppercase tracking-widest text-[#b08e22]">
              Rivera The Valley
            </span>
            <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-[#d2ac47]" />
          </motion.div>

          <motion.h2
            className="mb-4 bg-gradient-to-r from-[#b08e22] to-[#d2ac47] bg-clip-text text-4xl font-bold text-transparent md:text-5xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            Dream. Dare. Discover
          </motion.h2>
        </motion.div>

        <div className="flex flex-col items-stretch gap-12 lg:flex-row">
          {/* Content Cards */}
          <motion.div
            className="flex-1 space-y-8"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            {[
              {
                title: "Distinctive Architectural Elegance",
                description:
                  "Experience contemporary villa living with striking double-height foyers and spacious interiors designed to elevate everyday living.",
              },
              {
                title: "Nature-Infused Community Living",
                description:
                  "Immerse yourself in lush, landscaped surroundings featuring themed play areas, scenic valley trails, and serene riverside lounges.",
              },
              {
                title: "Trusted Excellence by Emaar",
                description:
                  "Developed by Emaar Properties, offering outstanding build quality, timeless design, and enduring value you can rely on.",
              },
              {
                title: "Eco-Conscious Amenities",
                description:
                  "From sports courts and dog parks to peaceful sunken seating areas, enjoy a versatile range of amenities tailored for all ages and interests.",
              },
            ].map((card, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-white/80 p-8 backdrop-blur-sm transition-all duration-300 hover:bg-white/90 hover:shadow-xl hover:shadow-[#d2ac47]/10"
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 * index }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#d2ac47]/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <h3 className="mb-4 text-2xl font-semibold text-[#2d3436]">
                  {card.title}
                </h3>
                <p className="text-[#636e72]">{card.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Image Grid */}
          <motion.div
            className="flex-1"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
          >
            <div className="grid grid-cols-2 gap-4 cursor-pointer">
              {[
                { title: "Kids Play Area", image: "/Images/StakePark.jpg" },
                { title: "Skate Park", image: "/Images/SportsVillage.jpg " },
                {
                  title: "Sports Village",
                  image: "/Images/RIVERA_THE_VALLEY_IMAGE13.jpg",
                },
                { title: "Jogging Route", image: "/Images/SportsVillage.jpg " },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="group relative h-48 overflow-hidden rounded-2xl md:h-64"
                  whileHover={{ scale: 1.05 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 * index }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 transition-all duration-300 group-hover:to-black/70" />
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-300"
                  />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block rounded-lg bg-white/90 px-3 py-1 text-sm font-medium text-[#2d3436] backdrop-blur-sm transition-all duration-300 group-hover:bg-[#d2ac47] group-hover:text-white">
                      {item.title}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Amenities Grid */}
            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 1 }}
            >
             
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                {amenities.map((item, index) => (
                  <motion.div
                    key={index}
                    className="group flex flex-col items-center justify-center rounded-xl bg-white/80 p-4 text-center backdrop-blur-sm transition-all duration-300 hover:bg-white hover:shadow-lg hover:shadow-[#d2ac47]/10 border border-[#b08e22] "
                    whileHover={{ scale: 1.05 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * index }}
                  ><BorderBeam size={400} />
                    {/* <span className="mb-2 text-3xl transition-transform duration-300 group-hover:scale-110">
                      {item.icon}
                    </span> */}
                    <span className="text-sm font-medium text-[#2d3436]">
                      {item.title}
                    </span>
                    <span className="mt-1 text-sm font-semibold text-[#b08e22]">
                      {item.value}
                    </span>
                    <BorderBeam size={400} />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SustainableLiving;
